/* eslint-disable max-lines */
import 'videojs-errors';
import 'videojs-mux';
import 'videojs-contrib-quality-levels';

import videojs, { VideoJsPlayerOptions } from 'video.js';
import { ITimeRange } from 'src/interfaces/TimeRange';
import { formatTimeRanges } from 'src/utils/formatUtils';
import { IVideoJSPlayer, IOptionsBeforeRequest } from 'src/interfaces/VideoJS';
import { exitPictureInPicture, isApple, isPictureInPicture } from 'src/utils/browserUtils';
import { EVENT_TYPES } from 'src/interfaces/enums';
import { IQualityLevel } from 'src/interfaces/Playlist';
import { formatMedia } from 'src/utils/playerUtils';
import { CodeErrors, IPlayerError } from 'src/interfaces/PlayerError';
import DRMService from 'src/services/DRMService';
import {
  IPlayerGateway,
  MediaInfo,
  VideoDimensions,
  VideoPlaybackQuality,
} from 'src/core/player/interfaces/PlayerGateway';
import { DEFAULT_RESOLUTION, DEFAULT_SOURCE } from './constants';

class VideoJsGateway implements IPlayerGateway {
  protected videojsInstance: IVideoJSPlayer;

  constructor(element: HTMLVideoElement, config: VideoJsPlayerOptions, callback?: () => void) {
    this.videojsInstance = videojs(element, config, callback) as IVideoJSPlayer;

    this.videojsInstance.errors();
    this.videojsInstance.errors.timeout(-1);

    window.PLAYER = this.videojsInstance;
  }

  get muxEmitter() {
    return this.videojsInstance.mux?.emit;
  }

  get currentSourceType() {
    return this.videojsInstance.currentSource()?.type;
  }

  get duration(): number {
    return this.videojsInstance.duration();
  }

  get currentTime() {
    return this.videojsInstance.currentTime() || 0;
  }

  get watchTimes(): ITimeRange[] {
    const timeRanges = this.videojsInstance.played();
    if (!timeRanges) return [];

    /**@todo Avaliar se todo Gateway dever ter um metodo para formatar o timeRange */
    return formatTimeRanges(timeRanges);
  }

  get lastIntervalWatched(): ITimeRange | undefined {
    const currentTime = this.currentTime;

    return this.watchTimes?.find((watchtime) => watchtime.end === currentTime);
  }

  get hasEnded(): boolean {
    return this.videojsInstance.ended();
  }

  get isMuted(): boolean {
    return this.videojsInstance.muted();
  }

  get isAudio(): boolean {
    return this.videojsInstance.isAudio();
  }

  get videoDimensions(): VideoDimensions {
    return {
      width: this.videojsInstance.videoWidth(),
      height: this.videojsInstance.videoHeight(),
    };
  }

  get playbackRate(): number {
    return this.videojsInstance.playbackRate();
  }

  get hasSupportToHLS(): boolean {
    return Boolean(this.videojsInstance.tech(true)?.vhs);
  }

  get hasSupportToNativeHLS(): boolean {
    return this.videojsInstance.tech(true)?.vhs ? false : true;
  }

  get mediaInfo(): MediaInfo {
    const attributes = this.videojsInstance.tech(true).vhs?.playlists?.media().attributes;

    const { BANDWIDTH, CODECS, RESOLUTION = { width: 0, height: 0 } } = attributes ?? {};
    return {
      bandwidth: BANDWIDTH || 0,
      codecs: CODECS || '',
      resolution: { width: RESOLUTION.width, height: RESOLUTION.height },
    };
  }

  play(): Promise<void> | undefined {
    return this.videojsInstance.play();
  }

  pause(): void {
    this.videojsInstance.pause();
  }

  paused(): boolean {
    return this.videojsInstance.paused();
  }

  hasStarted(): boolean {
    return this.videojsInstance.hasStarted();
  }

  setCurrentTime(seconds: number): void {
    this.videojsInstance.currentTime(seconds);
  }

  setVolume(volume: number): void {
    this.videojsInstance.volume(volume);
  }

  volume(): number {
    return this.videojsInstance.volume();
  }

  muted(): void {
    this.videojsInstance.muted(true);
  }

  unmuted(): void {
    this.videojsInstance.muted(false);
  }

  get userActive(): boolean {
    return this.videojsInstance.userActive() ?? false;
  }

  setUserActive(active: boolean): void {
    this.videojsInstance.userActive(active);
  }

  togglePictureInPicture(): void {
    if (isPictureInPicture()) {
      exitPictureInPicture();
    } else {
      this.videojsInstance.requestPictureInPicture();
    }
  }

  setPlaybackRate(rate: number): void {
    this.videojsInstance.playbackRate(rate);
  }

  clean() {
    this.videojsInstance.dispose();
  }

  addSubtitleListener(callback: () => void) {
    this.textTracks?.on(EVENT_TYPES.ADD_TRACK.toLowerCase(), callback);
  }

  addEventListener(eventType: EVENT_TYPES, callback: (...args: unknown[]) => void) {
    this.videojsInstance.on(this.videojsInstance.el(), eventType.toLowerCase(), callback);
  }

  removeEventListener(eventType: EVENT_TYPES, callback: (...args: unknown[]) => void) {
    this.videojsInstance.off(this.videojsInstance.el(), eventType.toLowerCase(), callback);
  }

  addFirstEventListener(eventType: EVENT_TYPES, callback: (...args: unknown[]) => void) {
    this.videojsInstance.one(this.videojsInstance.el(), eventType.toLowerCase(), callback);
  }

  get textTracks() {
    return this.videojsInstance.textTracks();
  }

  get buffered() {
    return this.videojsInstance.buffered();
  }

  get playlists() {
    const playlists = this.qualityLevels;
    const sources = [DEFAULT_SOURCE];

    playlists?.forEach((quality: IQualityLevel) => {
      return sources.push(formatMedia(quality));
    });

    return sources;
  }

  triggerEvent(eventType: EVENT_TYPES, ...args: unknown[]) {
    this.videojsInstance.trigger(eventType.toLowerCase(), ...args);
  }

  setResolution(currentResolution: string) {
    if (!this.videojsInstance) return;

    this.qualityLevels.forEach((quality: IQualityLevel) => {
      quality.enabled = quality.height === parseInt(currentResolution) || currentResolution === DEFAULT_RESOLUTION;
    });

    this.triggerEvent(EVENT_TYPES.RESOLUTION_CHANGE);
  }

  get qualityLevels(): IQualityLevel[] {
    return this.videojsInstance.qualityLevels().levels_;
  }

  get currentPlaylist() {
    const qualityLevel = this.qualityLevels.find((quality: IQualityLevel) => quality.enabled);
    return qualityLevel && formatMedia(qualityLevel);
  }

  activateSubtitle(active: boolean) {
    this.triggerEvent(EVENT_TYPES.SUBTITLE_CHANGE, active);
  }

  disableLoader() {
    this.videojsInstance.hasStarted(true);
    this.videojsInstance.updateSource;
    this.videojsInstance.ready();
  }

  setCustomErrorMessages(errors: { [code: string]: IPlayerError }) {
    this.videojsInstance.errors.extend({ ...errors });
  }

  getErrorMessage() {
    const error = this.videojsInstance.error();
    const errorMessages = this.videojsInstance.errors.getAll();

    if (!error || !errorMessages) {
      return null;
    }

    if (!errorMessages[error.code]) {
      return errorMessages[CodeErrors.UNKNOWN] as IPlayerError;
    }

    const errorMessage = errorMessages[error.code] as IPlayerError;
    return errorMessage;
  }

  el() {
    return this.videojsInstance.el();
  }

  readyState() {
    return this.videojsInstance.readyState();
  }

  get videoPlaybackQuality(): VideoPlaybackQuality {
    const { corruptedVideoFrames, creationTime, droppedVideoFrames, totalVideoFrames } = this.videojsInstance
      .tech(true)
      .getVideoPlaybackQuality();
    return { corruptedVideoFrames, creationTime, droppedVideoFrames, totalVideoFrames };
  }

  setupBeforeRequest(withCredentials: boolean) {
    if (!this.videojsInstance.tech(true).vhs) return;

    this.videojsInstance.tech(true).vhs.xhr.beforeRequest = (options: IOptionsBeforeRequest) => {
      options.withCredentials = withCredentials;
      return options;
    };
  }

  async enableDRM() {
    await import('videojs-contrib-eme');
    this.videojsInstance.eme();
  }

  initializeMediaKeys() {
    if (isApple()) return;
    this.videojsInstance.eme.initializeMediaKeys({}, DRMService.emeCallback, true);
  }
}

export default VideoJsGateway;
